import { toNumber, toOptionalNumber } from '@albacross/frontend-utils'

import { safeNumberFromString } from 'utils/strings'

export type CampaignCtr = { matchedClicks: number; matchedViews: number; percent: number }
export type CampaignPlan = { matchedViews: number; totalViews: number; percent: number }
export type CampaignCompletionDaily = { matchedViewsPerDay: number; totalViewsPerDay: number; percent: number }
export type CampaignTargetCtr = { companiesWithClicks: number; companiesWithViews: number; percent: number }
export type AdExchangeCollection = { matchedViews: number; exchangeTotalViews: number; percent: number }
export type AdExchangeCtr = { exchangeTotalClicks: number; exchangeTotalViews: number; percent: number }
export type AdExchangePlan = { exchangeTotalViews: number; rtbTotalViews: number; percent: number }
export type AdExchangeCompletionDaily = {
  exchangeTotalViewsPerDay: number
  rtbTotalViewsPerDay: number
  percent: number
}
export type AdExchangeViewability = { exchangeViewableViews: number; exchangeTotalViews: number; percent: number }
export type AdExchangeSpent = { cost: number; currency: string }
export type CampaignAssignedUser = { id: number; name: string; email: string }

export type CampaignDetails = {
  id: number
  customerId: number
  title: string
  customerName: string
  startDate: string
  endDate: string
  percent: number
  status: string
  countries: string[]
  ctr: CampaignCtr
  plan: CampaignPlan
  completionDaily: CampaignCompletionDaily
  targetCtr: CampaignTargetCtr
  paid: boolean
  dailyBudget: number
  totalBudget: number
  cpm: number
  extraViewsPercent: number
  currency: string
  customerCost: number
  goal: string
  uuid: string
  url: string
  adformId: string
  assignedUserId: number | null
}

export type CampaignAdExchangeDetails = {
  type: string
  collection: AdExchangeCollection
  ctr: AdExchangeCtr
  plan: AdExchangePlan
  completionDaily: AdExchangeCompletionDaily
  viewability: AdExchangeViewability
  spent: AdExchangeSpent
  region: string
}

export type Campaign = {
  details: CampaignDetails
  adExchangeDetails: CampaignAdExchangeDetails
}

export const assignableUsers: CampaignAssignedUser[] = [
  { id: 1515, name: 'Katarzyna Sutor', email: 'katarzyna.sutor@albacross.com' },
  { id: 1516, name: 'Katarzyna Waśniowska', email: 'katarzyna.wasniowska@albacross.com' },
]

export const assignableUsersOptions = assignableUsers.map(item => ({
  value: item.id,
  label: item.name,
}))

const ctrFromServer = (x?: any): CampaignCtr =>
  x
    ? { matchedClicks: x.matched_clicks, matchedViews: x.matched_views, percent: x.percent }
    : { matchedClicks: 0, matchedViews: 0, percent: 0 }

const planFromServer = (x?: any): CampaignPlan =>
  x
    ? { matchedViews: x.matched_views, totalViews: x.total_views, percent: x.percent }
    : { matchedViews: 0, totalViews: 0, percent: 0 }

const completionDailyFromServer = (x?: any): CampaignCompletionDaily =>
  x
    ? { matchedViewsPerDay: x.matched_views_per_day, totalViewsPerDay: x.total_views_per_day, percent: x.percent }
    : { matchedViewsPerDay: 0, totalViewsPerDay: 0, percent: 0 }

const targetCtrFromServer = (x?: any): CampaignTargetCtr =>
  x
    ? { companiesWithClicks: x.companies_with_clicks, companiesWithViews: x.companies_with_views, percent: x.percent }
    : { companiesWithClicks: 0, companiesWithViews: 0, percent: 0 }

const adExchangeCollectionFromServer = (x?: any): AdExchangeCollection =>
  x
    ? { matchedViews: x.matched_views, exchangeTotalViews: x.exchange_total_views, percent: x.percent }
    : { matchedViews: 0, exchangeTotalViews: 0, percent: 0 }

const adExchangeCtrFromServer = (x?: any): AdExchangeCtr =>
  x
    ? { exchangeTotalClicks: x.exchange_total_clicks, exchangeTotalViews: x.exchange_total_views, percent: x.percent }
    : { exchangeTotalClicks: 0, exchangeTotalViews: 0, percent: 0 }

const adExchangePlanFromServer = (x?: any): AdExchangePlan =>
  x
    ? { exchangeTotalViews: x.exchange_total_views, rtbTotalViews: x.rtb_total_views, percent: x.percent }
    : { exchangeTotalViews: 0, rtbTotalViews: 0, percent: 0 }

const adExchangeCompletionDailyFromServer = (x?: any): AdExchangeCompletionDaily =>
  x
    ? {
        exchangeTotalViewsPerDay: x.exchange_total_views_per_day,
        rtbTotalViewsPerDay: x.rtb_total_views_per_day,
        percent: x.percent,
      }
    : { exchangeTotalViewsPerDay: 0, rtbTotalViewsPerDay: 0, percent: 0 }

const adExchangeViewabilityFromServer = (x?: any): AdExchangeViewability =>
  x
    ? {
        exchangeViewableViews: x.exchange_viewable_views,
        exchangeTotalViews: x.exchange_total_views,
        percent: x.percent,
      }
    : { exchangeViewableViews: 0, exchangeTotalViews: 0, percent: 0 }

const adExchangeSpentFromServer = (x?: any): AdExchangeSpent =>
  x ? { cost: x.exchange_cost, currency: x.exchange_currency } : { cost: 0, currency: '' }

const emptyDetails = (): CampaignDetails => ({
  id: 0,
  customerId: 0,
  title: '',
  customerName: '',
  startDate: '',
  endDate: '',
  percent: 0,
  status: '',
  countries: [],
  ctr: ctrFromServer(),
  plan: planFromServer(),
  completionDaily: completionDailyFromServer(),
  targetCtr: targetCtrFromServer(),
  paid: false,
  dailyBudget: 0,
  totalBudget: 0,
  cpm: 0,
  extraViewsPercent: 0,
  currency: '',
  customerCost: 0,
  goal: '',
  uuid: '',
  url: '',
  adformId: '',
  assignedUserId: null,
})

// `?? default` back-fills omitted fields (matching the old Immutable Record defaults) so consumers get '' / 0 / null, not undefined.
const detailsFromServer = (x?: any): CampaignDetails =>
  x
    ? {
        id: x.id ?? 0,
        title: x.title ?? '',
        customerName: x.customer_name ?? '',
        customerId: x.customer_id ?? 0,
        startDate: x.date?.start_date ?? '',
        endDate: x.date?.end_date ?? '',
        percent: x.date?.percent ?? 0,
        status: x.status ?? '',
        countries: x.countries || [],
        ctr: ctrFromServer(x.ctr),
        plan: planFromServer(x.plan),
        completionDaily: completionDailyFromServer(x.completion_daily),
        targetCtr: targetCtrFromServer(x.target_ctr),
        paid: x.billing?.paid ?? false,
        totalBudget: x.billing?.budget_total ?? 0,
        dailyBudget: x.billing?.budget_daily ?? 0,
        cpm: x.billing?.cpm ?? 0,
        extraViewsPercent: x.billing?.exchange_extra_views_percent ?? 0,
        currency: x.billing?.currency ?? '',
        customerCost: x.billing?.customer_cost ?? 0,
        goal: x.goal ?? '',
        uuid: x.uuid ?? '',
        url: x.landing_page_url ?? '',
        adformId: x.adform_id ?? '',
        assignedUserId: x.adops_id ?? null,
      }
    : emptyDetails()

const adExchangeDetailsFromServer = (x?: any): CampaignAdExchangeDetails =>
  x
    ? {
        type: x.exchange_type ?? '',
        collection: adExchangeCollectionFromServer(x.collection),
        ctr: adExchangeCtrFromServer(x.ctr),
        plan: adExchangePlanFromServer(x.plan),
        completionDaily: adExchangeCompletionDailyFromServer(x.completion_daily),
        viewability: adExchangeViewabilityFromServer(x.viewability),
        spent: adExchangeSpentFromServer(x.spent),
        region: x.region ?? '',
      }
    : {
        type: '',
        collection: adExchangeCollectionFromServer(),
        ctr: adExchangeCtrFromServer(),
        plan: adExchangePlanFromServer(),
        completionDaily: adExchangeCompletionDailyFromServer(),
        viewability: adExchangeViewabilityFromServer(),
        spent: adExchangeSpentFromServer(),
        region: '',
      }

export const campaignFromServer = (x?: any): Campaign =>
  x
    ? {
        details: detailsFromServer(x.details),
        adExchangeDetails: adExchangeDetailsFromServer(x.ad_exchange_details),
      }
    : { details: detailsFromServer(), adExchangeDetails: adExchangeDetailsFromServer() }

export const campaignToServer = (x: any) => ({
  id: safeNumberFromString(x.id),
  title: x.title,
  url: x.url,
  end_date: x.endDate,
  start_date: x.startDate,
  currency: x.currency,
  budget_daily: toNumber(x.dailyBudget),
  cpm: toNumber(x.cpm),
  exchange_extra_views_percent: parseFloat(x.extraViewsPercent),
  adform_id: toOptionalNumber(x.adformId),
  status: x.status,
  adops_id: toOptionalNumber(x.assignedUserId),
  customer_id: toNumber(x.customer.id),
  customer: x.customer.name,
})
